import { isTouchDevice } from '../core/utils';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  opacity: number;
  originalVx: number;
  originalVy: number;
  pulse: number;
  currentSize: number;
}

interface Sparkle {
  x: number;
  y: number;
  size: number;
  opacity: number;
  decay: number;
}

const COLOR_SCHEMES: string[][] = [
  ['#ff2a6d', '#05d9e8', '#d300f9', '#e2e2e8', '#8b8b9a'],
  ['#ff2a6d', '#d300f9', '#05d9e8', '#ff6b9d', '#7b2ff7'],
  ['#05d9e8', '#00f5d4', '#d300f9', '#ff2a6d', '#fee440'],
  ['#d300f9', '#ff2a6d', '#05d9e8', '#9b5de5', '#f15bb5'],
];

const CONNECTION_DISTANCE = 120;
const MOUSE_REPEL_DISTANCE = 150;
const MAX_SPARKLES = 20;

export class ParticleGalaxy {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private sparkles: Sparkle[] = [];
  private mouse = { x: -1000, y: -1000 };
  private currentColorScheme = 0;
  private animationId = 0;
  private sparkleInterval = 0;
  private isTouch: boolean;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Cannot get 2d context');
    this.ctx = ctx;
    this.isTouch = isTouchDevice();

    this.init();
  }

  private init(): void {
    this.resizeCanvas();
    this.createParticles();
    this.setupEventListeners();
    this.animate();
    this.sparkleInterval = window.setInterval(() => this.createSparkle(), 500);
  }

  private resizeCanvas(): void {
    this.canvas.width = this.canvas.offsetWidth;
    this.canvas.height = this.canvas.offsetHeight;
  }

  private getParticleCount(): number {
    const area = this.canvas.width * this.canvas.height;
    const base = Math.floor(area / 6000);
    if (this.isTouch) return Math.min(50, base);
    return Math.min(180, base);
  }

  private createParticles(): void {
    this.particles = [];
    const count = this.getParticleCount();

    for (let i = 0; i < count; i++) {
      const vx = (Math.random() - 0.5) * 2;
      const vy = (Math.random() - 0.5) * 2;
      this.particles.push({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        vx,
        vy,
        size: Math.random() * 3 + 1,
        color: this.getRandomColor(),
        opacity: Math.random() * 0.8 + 0.2,
        originalVx: vx,
        originalVy: vy,
        pulse: Math.random() * Math.PI * 2,
        currentSize: 0,
      });
    }
  }

  private getRandomColor(): string {
    const colors = COLOR_SCHEMES[this.currentColorScheme];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  private setupEventListeners(): void {
    const onResize = (): void => {
      this.resizeCanvas();
      this.createParticles();
    };

    window.addEventListener('resize', onResize);

    const updateMouse = (clientX: number, clientY: number): void => {
      const rect = this.canvas.getBoundingClientRect();
      this.mouse.x = clientX - rect.left;
      this.mouse.y = clientY - rect.top;
    };

    const resetMouse = (): void => {
      this.mouse.x = -1000;
      this.mouse.y = -1000;
    };

    this.canvas.addEventListener('mousemove', (e: MouseEvent) => {
      updateMouse(e.clientX, e.clientY);
    });
    this.canvas.addEventListener('mouseleave', resetMouse);

    this.canvas.addEventListener('touchstart', (e: TouchEvent) => {
      if (e.touches.length === 1) updateMouse(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    this.canvas.addEventListener('touchmove', (e: TouchEvent) => {
      if (e.touches.length === 1) updateMouse(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    this.canvas.addEventListener('touchend', resetMouse);
    this.canvas.addEventListener('touchcancel', resetMouse);
  }

  private createSparkle(): void {
    if (this.isTouch) return;
    if (this.sparkles.length < MAX_SPARKLES) {
      this.sparkles.push({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        size: Math.random() * 2 + 1,
        opacity: 1,
        decay: Math.random() * 0.02 + 0.01,
      });
    }
  }

  private updateParticles(): void {
    for (const particle of this.particles) {
      const dx = this.mouse.x - particle.x;
      const dy = this.mouse.y - particle.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < MOUSE_REPEL_DISTANCE) {
        const force = (MOUSE_REPEL_DISTANCE - distance) / MOUSE_REPEL_DISTANCE;
        const angle = Math.atan2(dy, dx);
        particle.vx -= Math.cos(angle) * force * 0.5;
        particle.vy -= Math.sin(angle) * force * 0.5;
      } else {
        particle.vx += (particle.originalVx - particle.vx) * 0.02;
        particle.vy += (particle.originalVy - particle.vy) * 0.02;
      }

      particle.x += particle.vx;
      particle.y += particle.vy;

      if (particle.x < 0) particle.x = this.canvas.width;
      if (particle.x > this.canvas.width) particle.x = 0;
      if (particle.y < 0) particle.y = this.canvas.height;
      if (particle.y > this.canvas.height) particle.y = 0;

      particle.pulse += 0.02;
      particle.currentSize = particle.size + Math.sin(particle.pulse) * 0.5;
    }
  }

  private drawParticles(): void {
    for (const particle of this.particles) {
      this.ctx.save();
      this.ctx.globalAlpha = particle.opacity;
      this.ctx.fillStyle = particle.color;
      if (!this.isTouch) {
        this.ctx.shadowBlur = 15;
        this.ctx.shadowColor = particle.color;
      }
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, particle.currentSize, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.restore();
    }
  }

  private getConnectionDistance(): number {
    return this.isTouch ? 80 : CONNECTION_DISTANCE;
  }

  private drawConnections(): void {
    const connectionDistance = this.getConnectionDistance();
    const len = this.particles.length;
    for (let i = 0; i < len; i++) {
      for (let j = i + 1; j < len; j++) {
        const p1 = this.particles[i];
        const p2 = this.particles[j];
        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < connectionDistance) {
          const opacity = (connectionDistance - distance) / connectionDistance * 0.3;
          this.ctx.save();
          this.ctx.globalAlpha = opacity;
          this.ctx.strokeStyle = p1.color;
          this.ctx.lineWidth = 1;
          this.ctx.beginPath();
          this.ctx.moveTo(p1.x, p1.y);
          this.ctx.lineTo(p2.x, p2.y);
          this.ctx.stroke();
          this.ctx.restore();
        }
      }
    }
  }

  private drawMouseEffect(): void {
    if (this.mouse.x === -1000) return;

    const gradient = this.ctx.createRadialGradient(
      this.mouse.x, this.mouse.y, 0,
      this.mouse.x, this.mouse.y, 100
    );
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.1)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    this.ctx.save();
    this.ctx.fillStyle = gradient;
    this.ctx.beginPath();
    this.ctx.arc(this.mouse.x, this.mouse.y, 100, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.restore();
  }

  private drawSparkles(): void {
    this.sparkles = this.sparkles.filter((sparkle) => {
      sparkle.opacity -= sparkle.decay;
      if (sparkle.opacity > 0) {
        this.ctx.save();
        this.ctx.globalAlpha = sparkle.opacity;
        this.ctx.fillStyle = '#ffffff';
        if (!this.isTouch) {
          this.ctx.shadowBlur = 10;
          this.ctx.shadowColor = '#ffffff';
        }
        this.ctx.beginPath();
        this.ctx.arc(sparkle.x, sparkle.y, sparkle.size, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.restore();
        return true;
      }
      return false;
    });
  }

  private animate(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.updateParticles();
    this.drawConnections();
    this.drawParticles();
    this.drawMouseEffect();
    this.drawSparkles();
    this.animationId = requestAnimationFrame(() => this.animate());
  }

  dispose(): void {
    cancelAnimationFrame(this.animationId);
    clearInterval(this.sparkleInterval);
  }
}
